import { MessageCircle } from 'lucide-react';

const FacebookIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path></svg>
);

const InstagramIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg>
);

export default function MaintenancePage() {
  return (
    <div
      dir="rtl"
      className="min-h-screen w-full flex items-center justify-center bg-zinc-950 relative overflow-hidden"
    >
      {/* Background noise texture */}
      <div className="absolute inset-0 bg-[url('https://transparenttextures.com/patterns/cubes.png')] opacity-[0.03] pointer-events-none" />

      {/* Centered red glow orb */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-[700px] h-[700px] bg-primary-500/20 rounded-full blur-[140px]" />
      </div>

      {/* Subtle top glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[400px] h-[2px] bg-gradient-to-r from-transparent via-primary-500/60 to-transparent" />

      {/* Glassmorphic Card */}
      <div className="relative z-10 w-full max-w-xl mx-4">
        <div className="bg-white/5 backdrop-blur-2xl border border-white/10 rounded-3xl p-10 md:p-14 shadow-[0_0_80px_rgba(224,0,0,0.08)] text-center flex flex-col items-center gap-8 animate-in fade-in zoom-in-95 duration-700">

          {/* Logo */}
          <div className="relative">
            <div className="absolute -inset-4 bg-primary-500/15 rounded-full blur-xl pointer-events-none animate-pulse" />
            <img
              src="/lobab.png"
              alt="شعار لباب سكول"
              className="relative h-20 w-auto object-contain drop-shadow-[0_0_20px_rgba(224,0,0,0.4)]"
            />
          </div>

          {/* Heading */}
          <div className="space-y-3">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-primary-500/10 border border-primary-500/20 rounded-full text-primary-400 text-sm font-bold tracking-widest uppercase">
              <span className="w-2 h-2 rounded-full bg-primary-500 animate-pulse" />
              وضع الصيانة
            </div>
            <h1 className="text-3xl md:text-4xl font-black text-white leading-tight">
              نحن نقوم بتحديث المنصة 🚀
            </h1>
            <p className="text-zinc-400 text-lg font-medium leading-relaxed max-w-sm mx-auto">
              نعمل بجدّ على تطوير تجربتك التعليمية وتحسين المنصة لتستحق أفضل ما يمكن تقديمه.
              <br />
              <span className="text-zinc-300 font-bold">نعود إليكم قريباً 🤍</span>
            </p>
          </div>

          {/* Divider */}
          <div className="w-full flex items-center gap-4">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-zinc-500 text-sm font-bold shrink-0">يمكنكم التواصل معنا عبر</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          {/* Social Buttons */}
          <div className="flex items-center justify-center gap-4 flex-wrap">
            {/* WhatsApp */}
            <a
              href="https://wa.me/967773939009"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-3 px-6 py-3.5 bg-white/5 hover:bg-[#25D366]/15 border border-white/10 hover:border-[#25D366]/40 rounded-2xl font-bold text-zinc-300 hover:text-[#25D366] transition-all duration-300 hover:shadow-[0_0_30px_rgba(37,211,102,0.15)] hover:-translate-y-0.5"
              aria-label="تواصل معنا عبر واتساب"
            >
              <MessageCircle className="w-5 h-5 transition-transform group-hover:scale-110" />
              واتساب
            </a>

            {/* Facebook */}
            <a
              href="https://www.facebook.com/lobab.school"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-3 px-6 py-3.5 bg-white/5 hover:bg-[#1877F2]/15 border border-white/10 hover:border-[#1877F2]/40 rounded-2xl font-bold text-zinc-300 hover:text-[#1877F2] transition-all duration-300 hover:shadow-[0_0_30px_rgba(24,119,242,0.15)] hover:-translate-y-0.5"
              aria-label="تابعوانا على فيسبوك"
            >
              <FacebookIcon />
              فيسبوك
            </a>

            {/* Instagram */}
            <a
              href="https://www.instagram.com/lobab.school/"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-3 px-6 py-3.5 bg-white/5 hover:bg-pink-500/15 border border-white/10 hover:border-pink-500/40 rounded-2xl font-bold text-zinc-300 hover:text-pink-400 transition-all duration-300 hover:shadow-[0_0_30px_rgba(236,72,153,0.15)] hover:-translate-y-0.5"
              aria-label="تابعونا على انستجرام"
            >
              <InstagramIcon />
              انستجرام
            </a>
          </div>

          {/* Footer note */}
          <p className="text-zinc-600 text-sm font-medium">
            © {new Date().getFullYear()} لباب سكول — جميع الحقوق محفوظة
          </p>
        </div>
      </div>
    </div>
  );
}
